<template>
  <div class="user-offer-system">
    <!-- Auto-save loading overlay -->
    <v-overlay :value="isSavingExercise" absolute>
      <div class="text-center">
        <v-progress-circular indeterminate color="amber" size="64" />
        <p class="mt-4 white--text">Saving exercise...</p>
      </div>
    </v-overlay>

    <v-row>
      <v-col cols="12">
        <exercise-logic-id ref="exerciseLogic" exCollection="offerSystemExCollection" refCollection="" :doc="doc"
          :shared="false" :projectShared="false" :pid="true" :eid="$route.params.eid" :synced="false" @ready="onReady"
          @error="onError" @saved="onSaved" @savedNext="onSavedNext" @loadedEx="onLoadedEx" @modifyEx="onModifyEx"
          @loadedRef="onLoadedRef">
          <template slot-scope="{ saveExercise }">
            <!-- Header Controls -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h2 class="headline mb-0">Offer Eco-System</h2>
              <div class="d-flex align-center ga-1">
                <v-btn-toggle v-model="adminMode" mandatory dense>
                  <v-btn value="participant" small>
                    <v-icon small left>mdi-view-grid</v-icon>
                    Participant
                  </v-btn>
                  <v-btn value="admin" small>
                    <v-icon small left>mdi-view-list</v-icon>
                    Admin
                  </v-btn>
                </v-btn-toggle>
                <v-btn icon title="Settings" @click="settingsDialog = true">
                  <v-icon>mdi-cog-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <v-row>
              <v-col cols="12">
                <v-text-field v-model.trim="doc.title" label="Title" color="amber" />
              </v-col>
            </v-row>

            <!-- Stepper Navigation -->
            <v-stepper v-model="currentStep">
              <v-stepper-header>
                <template v-for="(step, idx) in activeTabs">
                  <v-stepper-step :key="`step-${step.id}`" :step="idx + 1" :complete="currentStep > idx + 1"
                    color="amber" @click="currentStep = idx + 1">
                    {{ step.label }}
                  </v-stepper-step>
                  <v-divider v-if="idx < steps.length - 1" :key="`div-${step.id}`" />
                </template>
              </v-stepper-header>

              <v-stepper-items>
                <!-- Step 1: Define Groups-->
                <v-stepper-content step="1">
                  <div class="d-flex justify-space-between align-center mb-4">

                    <v-chip small class="">{{ doc.list.length }} group{{ doc.list.length === 1 ? '' : 's'
                    }}</v-chip>
                    <div class="d-flex align-center ga-1">
                      <v-btn color="amber" light @click="openAddGroup">
                        Add Group
                      </v-btn>
                      <v-btn icon title="Settings" @click="settingsDialog = true">
                        <v-icon>mdi-cog-outline</v-icon>
                      </v-btn>
                    </div>

                  </div>

                  <v-row v-if="doc.list.length">
                    <v-col v-for="group in doc.list" :key="group.id" cols="12" sm="6" md="4">
                      <v-card light outlined class="group-card" @click="openEditGroup(group.id)">
                        <v-card-title class="d-flex justify-space-between align-start">
                          <span class="text-truncate">{{ group.title || 'Untitled' }}</span>
                          <div class="group-card__actions">
                            <v-btn icon x-small title="Edit" @click.stop="openEditGroup(group.id)">
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon x-small title="Delete" @click.stop="deleteGroup(group.id)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-card-title>
                        <v-card-text>
                          <p class="text-body-2 mb-1">
                            {{ (group.children && group.children.length) || 0 }} item{{ (group.children &&
                              group.children.length) === 1 ? '' : 's' }}
                          </p>
                          <p v-if="group.description" class="text-caption grey--text mb-0 text-truncate">
                            {{ group.description }}
                          </p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-card v-else outlined class="pa-6 text-center">
                    <p class="text-h6 mb-2">No groups yet</p>
                    <p class="text-body-2 grey--text mb-0">Add a group to organize your items.</p>
                  </v-card>

                  <bottom-navigation :save="true" :next="true" :prev="false" :gridView="false" :gridToggle="false"
                    list="offer-system-v2" @save="saveExercise(true)" @next="saveExercise(false)" />
                </v-stepper-content>
                <!-- Step 2: Define Items -->
                <v-stepper-content step="2">
                  <div class="d-flex justify-space-between align-center mb-4 ">
                    <div class="d-flex align-center">
                      <v-text-field v-model="cardSearch" dense outlined hide-details label="Search items"
                        prepend-inner-icon="mdi-magnify" style="max-width: 300px" clearable />
                      <v-chip small class="ml-3">{{ activeCards.length }} item{{ activeCards.length === 1 ? '' : 's'
                      }}</v-chip>
                    </div>


                    <div class="d-flex align-center ga-1">
                      <v-btn light outlined color="grey" class="step2-add-btn" @click.stop="openAddGroup()">
                        <v-icon small left>mdi-plus</v-icon>
                        Add Group
                      </v-btn>

                      <v-btn icon title="Settings" @click="settingsDialog = true">
                        <v-icon>mdi-cog-outline</v-icon>
                      </v-btn>
                    </div>

                  </div>

                  <!-- No groups message -->
                  <v-card v-if="!doc.list.length" outlined class="pa-6 text-center mb-4">
                    <p class="text-h6 mb-2">No groups defined</p>
                    <p class="text-body-2 grey--text mb-4">Create groups in Step 1 first, then add items to them here.
                    </p>
                    <v-btn color="amber" light @click="currentStep = 1">Go to Step 1</v-btn>
                  </v-card>

                  <!-- Expansion Panels by Group -->
                  <v-expansion-panels light v-else v-model="step2Panels" multiple>
                    <v-expansion-panel v-for="group in doc.list" :key="group.id">
                      <v-expansion-panel-header class="step2-panel-header">
                        <div class="d-flex align-center justify-space-between flex-grow-1 mr-4">
                          <div class="d-flex align-center">
                            <span class="text-subtitle-1 font-weight-medium">{{ group.title || 'Untitled' }}</span>
                            <v-chip x-small class="ml-2">
                              {{ (filteredCardsByGroup[group.id] || []).length }}
                            </v-chip>
                            <v-btn icon x-small class="ml-2" title="Edit Group" @click.stop="openEditGroup(group.id)">
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon x-small title="Delete Group" @click.stop="deleteGroup(group.id)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>


                          </div>

                          <v-btn light color="amber" class="step2-add-btn" @click.stop="openAddCardIntoGroup(group.id)">
                            <v-icon small left>mdi-plus</v-icon>
                            Add Item
                          </v-btn>




                        </div>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content>
                        <v-row v-if="(filteredCardsByGroup[group.id] || []).length">
                          <v-col v-for="card in filteredCardsByGroup[group.id]" :key="card.id" cols="12" sm="6" md="4">
                            <v-card light outlined class="item-card" @click="openEditCard(card.id)">
                              <v-card-title class="d-flex justify-space-between align-start py-2">
                                <span class="text-truncate">{{ card.name || card.title || 'Untitled'
                                }}</span>
                                <div class="item-card__actions">
                                  <v-btn icon x-small title="Edit" @click.stop="openEditCard(card.id)">
                                    <v-icon small>mdi-pencil</v-icon>
                                  </v-btn>
                                  <v-btn icon x-small title="Delete" @click.stop="confirmDeleteCard(card.id)">
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </div>
                              </v-card-title>
                              <v-card-text class="py-2">
                                <p class="text-body-2 mb-1 text-truncate">{{ card.sentence || '—' }}</p>
                                <v-chip v-if="isCardPlaced(card.id)" x-small color="green" text-color="white">
                                  Placed
                                </v-chip>
                                <v-chip v-else x-small>Unplaced</v-chip>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>
                        <p v-else class="text-body-2 grey--text text-center py-4">
                          No items in this group yet. Click "Add Item" to create one.
                        </p>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Unassigned Items Panel -->
                    <v-expansion-panel v-if="(filteredCardsByGroup.unassigned || []).length">
                      <v-expansion-panel-header class="step2-panel-header">
                        <div class="d-flex align-center justify-space-between flex-grow-1 mr-4">
                          <div class="d-flex align-center">
                            <span class="text-subtitle-1 font-weight-medium grey--text">Unassigned</span>
                            <v-chip x-small class="ml-2">
                              {{ (filteredCardsByGroup.unassigned || []).length }}
                            </v-chip>
                          </div>
                        </div>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content>
                        <v-row>
                          <v-col v-for="card in filteredCardsByGroup.unassigned" :key="card.id" cols="12" sm="6" md="4">
                            <v-card light outlined class="item-card item-card--unassigned"
                              @click="openEditCard(card.id)">
                              <v-card-title class="d-flex justify-space-between align-start py-2">
                                <span class="text-truncate text-subtitle-2">{{ card.name || card.title || 'Untitled'
                                }}</span>
                                <div class="item-card__actions">
                                  <v-btn icon x-small title="Edit" @click.stop="openEditCard(card.id)">
                                    <v-icon small>mdi-pencil</v-icon>
                                  </v-btn>
                                  <v-btn icon x-small title="Delete" @click.stop="confirmDeleteCard(card.id)">
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </div>
                              </v-card-title>
                              <v-card-text class="py-2">
                                <p class="text-body-2 mb-1 text-truncate">{{ card.sentence || '—' }}</p>
                                <v-chip x-small color="warning" text-color="white">Needs Group</v-chip>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <bottom-navigation :save="true" :next="adminMode === 'admin'" :prev="true" :gridView="false"
                    :gridToggle="false" list="offer-system-v2" @save="saveExercise(true)" @next="saveExercise(false)"
                    @prev="prevStep()" />
                </v-stepper-content>

                <!-- Step 3: Arrange -->
                <v-stepper-content step="3">

                  <div class="d-flex justify-space-between align-center mb-4 ">
                    <div class="d-flex align-center">
                      <v-btn light :outlined="!minimalView" :color="minimalView ? 'amber' : 'grey'" small
                        @click="minimalView = !minimalView">
                        <v-icon small left>{{ minimalView ? 'mdi-text-short' : 'mdi-text' }}</v-icon>
                        {{ minimalView ? 'Minimal' : 'Full' }}
                      </v-btn>
                    </div>
                    <div class="d-flex align-center ga-1">
                      <v-btn light outlined color="grey" class="step2-add-btn" @click.stop="openAddGroup()">
                        <v-icon small left>mdi-plus</v-icon>
                        Add Group
                      </v-btn>

                      <v-btn icon title="Settings" @click="settingsDialog = true">
                        <v-icon>mdi-cog-outline</v-icon>
                      </v-btn>
                    </div>

                  </div>


                  <v-sheet :color="themeColor" class="mt-4 pa-4">
                    <draggable :list="doc.list" :class="doc.cols === 'auto' ? 'auto' : 'row'" group="outer" id="iso">
                      <structure-col v-for="(group, index) in doc.list" :key="group.id || `group-${index}`"
                        v-model="group.children" :item="group" :cardsById="cardsById" :attrs="structureColAttrs"
                        class="iso-item" @editGroup="openEditGroup" @onDeleteList="onDeleteList"
                        @addCard="openAddCardIntoGroup" @editCard="openEditCard" @deleteCard="softDeleteCard"
                        @cloneCard="cloneCard" @cardMoved="onCardMoved" />
                    </draggable>
                  </v-sheet>

                  <!-- Deleted Cards Section -->
                  <template v-if="deletedCards.length">
                    <h2 class="subtitle-2 mt-5">Deleted items</h2>
                    <structure-col :item="{ id: 'deleted', title: 'Deleted Items', name: 'deleted' }"
                      :value="deletedCardNodes" :cardsById="cardsById"
                      :attrs="{ ...structureColAttrs, add: false, controls: false }" @editCard="openEditCard" />
                  </template>

                  <bottom-navigation :save="true" :next="true" :prev="true" :gridView="false" :gridToggle="false"
                    list="offer-system-v2" @save="saveExercise(true)" @next="saveExercise(false)" />
                </v-stepper-content>

                <!-- Step 4: View (Read-only) -->
                <v-stepper-content step="4">
                  <!-- View Controls -->
                  <div class="d-flex justify-space-between align-center mb-4 flex-wrap">
                    <div class="d-flex align-center ga-2">
                      <v-btn-toggle v-model="viewMode" mandatory dense>
                        <v-btn value="grouped" small>
                          <v-icon small left>mdi-view-grid</v-icon>
                          Grouped
                        </v-btn>
                        <v-btn value="flat" small>
                          <v-icon small left>mdi-view-list</v-icon>
                          Flat
                        </v-btn>
                      </v-btn-toggle>

                      <v-select v-model="selectedGroups" :items="groupFilterOptions" item-text="title" item-value="id"
                        label="Filter by groups" multiple clearable dense outlined hide-details
                        style="max-width: 300px; min-width: 200px;" class="ml-2">
                        <template v-slot:selection="{ item, index }">
                          <v-chip v-if="index < 2" x-small>{{ item.title }}</v-chip>
                          <span v-if="index === 2" class="text-caption ml-1">
                            (+{{ selectedGroups.length - 2 }})
                          </span>
                        </template>
                      </v-select>
                    </div>

                    <div class="d-flex align-center">
                      <v-chip small class="mr-2">
                        {{ flatViewCards.length }} item{{ flatViewCards.length === 1 ? '' : 's' }}
                      </v-chip>
                      <v-btn icon title="Settings" @click="settingsDialog = true">
                        <v-icon>mdi-cog-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Grouped View -->
                  <v-sheet v-if="viewMode === 'grouped'" :color="themeColor" class="pa-4">
                    <v-row :class="doc.cols === 'auto' ? 'auto' : ''">
                      <structure-col v-for="(group, index) in filteredViewGroups"
                        :key="group.id || `view-group-${index}`" :value="group.children" :item="group"
                        :cardsById="cardsById" :attrs="viewColAttrs" class="iso-item" />
                    </v-row>
                  </v-sheet>

                  <!-- Flat View -->
                  <v-sheet v-else :color="themeColor" class="pa-4">
                    <v-row>
                      <v-col v-for="card in flatViewCards" :key="card.id"
                        :cols="doc.cols === 'auto' ? 'auto' : doc.cols">
                        <v-card light outlined class="flat-view-card">
                          <v-card-title class="py-2">
                            <div v-if="card.color" class="color-dot mr-2" :style="{ backgroundColor: card.color }" />
                            <span class="text-subtitle-1">{{ card.name || card.title || 'Untitled' }}</span>
                            <span v-if="card.spotlight" class="ml-1">*</span>
                          </v-card-title>

                          <v-card-text v-if="card.sentence" class="py-2">
                            {{ card.sentence }}
                          </v-card-text>

                          <!-- Tags Section -->
                          <v-card-text v-if="doc.tags" class="py-2">
                            <div v-if="card.categories && card.categories.length" class="mb-2">
                              <p class="text-caption text-uppercase mb-1">Categories</p>
                              <v-chip v-for="(cat, i) in card.categories" :key="`cat-${i}`" x-small
                                class="mr-1 mb-1 yellow lighten-3">{{ cat }}</v-chip>
                            </div>
                            <div v-if="card.assets && card.assets.length" class="mb-2">
                              <p class="text-caption text-uppercase mb-1">Assets</p>
                              <v-chip v-for="(asset, i) in card.assets" :key="`asset-${i}`" x-small
                                class="mr-1 mb-1 green lighten-4">{{ asset }}</v-chip>
                            </div>
                            <div v-if="card.tags && card.tags.length" class="mb-2">
                              <p class="text-caption text-uppercase mb-1">Tags</p>
                              <v-chip v-for="(tag, i) in card.tags" :key="`tag-${i}`" x-small
                                class="mr-1 mb-1 yellow lighten-5">{{
                                  tag }}</v-chip>
                            </div>
                            <div v-if="card.audiences && card.audiences.length" class="mb-2">
                              <p class="text-caption text-uppercase mb-1">Audiences</p>
                              <v-chip v-for="(aud, i) in card.audiences" :key="`aud-${i}`" x-small
                                class="mr-1 mb-1 yellow lighten-3">{{ aud }}</v-chip>
                            </div>
                            <div v-if="card.features && card.features.length" class="mb-2">
                              <p class="text-caption text-uppercase mb-1">Features</p>
                              <v-chip v-for="(feat, i) in card.features" :key="`feat-${i}`" x-small
                                class="mr-1 mb-1 yellow lighten-3">{{ feat }}</v-chip>
                            </div>
                            <div v-if="card.benefits && card.benefits.length">
                              <p class="text-caption text-uppercase mb-1">Benefits</p>
                              <v-chip v-for="(ben, i) in card.benefits" :key="`ben-${i}`" x-small
                                class="mr-1 mb-1 yellow lighten-3">{{ ben }}</v-chip>
                            </div>
                          </v-card-text>

                          <!-- Description -->
                          <v-card-text v-if="doc.note && card.body" class="py-2">
                            <p class="text-caption text-uppercase mb-1">Description</p>
                            <div class="pre-wrap">{{ card.body }}</div>
                          </v-card-text>

                          <!-- Historical Narrative -->
                          <v-card-text v-if="doc.historic && card.historical" class="py-2">
                            <p class="text-caption text-uppercase mb-1">Historical Narrative</p>
                            <div class="pre-wrap">{{ card.historical }}</div>
                          </v-card-text>

                          <!-- Graph -->
                          <v-card-text v-if="doc.graph && hasNonZeroScores(card.scores)" class="py-2">
                            <BarRadar :data="card.scores" :id="`flat-radar-${card.id}`" />
                          </v-card-text>

                          <!-- Stats -->
                          <v-card-text v-if="doc.stats && card.scores" class="py-2">
                            <div v-for="score in scoreFields" :key="score.key">
                              <p class="ma-0 pa-0">{{ score.label }}</p>
                              <v-slider v-model="card.scores[score.key]" :min="0" :max="10" step="1" ticks="always"
                                tick-size="4" color="amber" track-color="grey lighten-2" class="ma-0 pa-0" hide-details
                                readonly />
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-sheet>

                  <bottom-navigation :save="true" :next="false" :prev="true" :gridView="false" :gridToggle="false"
                    list="offer-system-v2" @save="saveExercise(true)" @prev="prevStep()" />
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </template>
        </exercise-logic-id>
      </v-col>
    </v-row>

    <!-- Add/Edit Card Dialog -->
    <v-dialog v-model="cardDialog" max-width="1200">
      <v-card>
        <!-- <v-card-title class="headline">
          {{ isEditingCard ? 'Edit item' : 'Add item' }}
        </v-card-title> -->
        <v-card-text>
          <item-form ref="cardDialogForm" :doc="doc" :item="editingCard" :default-group="targetGroupId"
            @submit="upsertCard" @cancel="closeCardDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Group Dialog -->
    <v-dialog v-model="groupDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          {{ isEditingGroup ? 'Edit group' : 'Add group' }}
        </v-card-title>
        <v-card-text>
          <col-form ref="groupDialogForm" :item="editingGroup" @submit="upsertGroup" @cancel="closeGroupDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Group Confirmation Dialog -->
    <v-dialog v-model="deleteGroupDialog" max-width="450">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert</v-icon>
          Delete Group?
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            Are you sure you want to delete <strong>"{{ deletingGroup ? deletingGroup.title : '' }}"</strong>?
          </p>
          <v-alert v-if="deletingGroupCardCount > 0" type="warning" dense text class="mb-0">
            This group contains <strong>{{ deletingGroupCardCount }}</strong>
            item{{ deletingGroupCardCount === 1 ? '' : 's' }} (including nested items).
            All items will be moved to the <strong>Deleted Items</strong> section and can be recovered later.
          </v-alert>
          <p v-else class="text-body-2 grey--text mb-0">
            This group is empty. The group structure will be preserved in case you need to restore it.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDeleteGroup">Cancel</v-btn>
          <v-btn color="red" text @click="executeDeleteGroup">
            <v-icon left small>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Card Confirmation Dialog -->
    <v-dialog v-model="deleteCardDialog" max-width="450">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert</v-icon>
          Delete Item?
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            Are you sure you want to delete <strong>"{{ deletingCard ? (deletingCard.name || deletingCard.title ||
              'Untitled') : '' }}"</strong>?
          </p>
          <p class="text-body-2 grey--text mb-0">
            The item will be moved to the Deleted Items section and can be recovered later.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDeleteCard">Cancel</v-btn>
          <v-btn color="red" text @click="executeDeleteCard">
            <v-icon left small>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Saved Confirmation Dialog -->
    <v-dialog v-model="savedDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ $t('exercise_service.saved.title') }}
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn color="amber" text @click="savedDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Settings Dialog -->
    <v-dialog v-model="settingsDialog" max-width="900">
      <v-card>
        <v-card-title class="headline d-flex justify-space-between align-center">
          <span>Settings</span>
          <v-btn color="amber" @click="settingsDialog = false" light>Apply</v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">

              <v-select v-model="doc.cols" label="Column width" :items="columnOptions" />
              <v-select v-model="doc.theme" label="Theme" :items="themeOptions" />
              <v-select v-model="doc.type" label="Type" :items="typeOptions" />

            </v-col>

            <v-col cols="8">
              <div class="d-flex flex-wrap">
                <v-switch v-for="toggle in toggleOptions" :key="toggle.key" v-model="doc[toggle.key]"
                  :label="toggle.label" color="amber" class="mr-3 ml-3" inset />
              </div>
            </v-col>

            <v-col cols="6">
              <unit-sub-items-with-id v-model="doc.local.categories" id-prefix="cat" :dark="false" class="mb-4">
                <v-label light>Define the categories for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>

              <unit-sub-items-with-id v-model="doc.local.tags" id-prefix="tag" :dark="false">
                <v-label light>Define the tags for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>
            </v-col>

            <v-col cols="6">
              <unit-sub-items-with-id v-model="doc.local.assets" id-prefix="asset" :dark="false" class="mb-4">
                <v-label light>Define the assets for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>

              <unit-sub-items-with-id v-model="doc.local.audiences" id-prefix="aud" :dark="false">
                <v-label light>Define the audiences for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>
            </v-col>
          </v-row>


        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="amber" @click="settingsDialog = false" light>Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Debug Panel (only in development) -->
    <v-expansion-panels v-if="isDev" class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>Debug Info</v-expansion-panel-header>
        <v-expansion-panel-content>
          <pre class="debug-pre">{{ debugInfo }}</pre>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'

import { BottomNavigation } from '@/components/core'
import { ExerciseLogicId } from '@/components/exercises'
import { BarRadar } from '@/components/graphs'
import { StructureCol, ItemForm, ColForm, UnitSubItemsWithId } from '@/views/user/exercises/offer-system-tabs'
import { docModel, deepClone, generateId, scoreFields } from '@/views/user/exercises/offer-system-tabs/model'

const fb = require('@/firebaseConfig.js')

// View-specific constants
const STEPS = [
  { id: 'groups', label: 'Define the groupings' },
  { id: 'define', label: 'Define the offer' },
  { id: 'arrange', label: 'Arrange' },
  { id: 'view', label: 'View' },
]

const COLUMN_OPTIONS = ['1', '2', '3', '4', '6', '12', 'auto']
const THEME_OPTIONS = ['dark', 'light']
const TYPE_OPTIONS = ['large', 'medium', 'small', 'waterfall']

const TOGGLE_OPTIONS = [
  { key: 'controls', label: 'Controls' },
  { key: 'note', label: 'Description' },
  { key: 'tags', label: 'Tags' },
  { key: 'historic', label: 'Historic' },
  { key: 'contribution', label: 'Contribution' },
  { key: 'stats', label: 'Stats' },
  { key: 'graph', label: 'Graph' },
  { key: 'children', label: 'Children' },
  { key: 'edit', label: 'Edit' },
]

export default {
  name: 'UserOfferSystemId',

  components: {
    BottomNavigation,
    ExerciseLogicId,
    StructureCol,
    UnitSubItemsWithId,
    ItemForm,
    ColForm,
    BarRadar,
    draggable,
  },

  data() {
    return {
      // Stepper
      currentStep: 1,
      steps: STEPS,

      // Options
      columnOptions: COLUMN_OPTIONS,
      themeOptions: THEME_OPTIONS,
      typeOptions: TYPE_OPTIONS,
      toggleOptions: TOGGLE_OPTIONS,
      scoreFields: scoreFields,

      // UI State
      savedDialog: false,
      cardDialog: false,
      groupDialog: false,
      settingsDialog: false,
      deleteGroupDialog: false,
      deleteCardDialog: false,
      cardSearch: '',
      step2Panels: [0],
      isSavingExercise: false,
      isAutoSaving: false,

      // Arrange Tab State
      minimalView: false, // Show only titles in arrange tab

      // View Tab State
      viewMode: 'grouped', // 'grouped' or 'flat'
      adminMode: 'participant', // 'participant' or 'admin'
      selectedGroups: [], // Array of group IDs to filter (empty = show all)

      // Editing State
      editingCardId: null,
      editingGroupId: null,
      targetGroupId: null,
      deletingGroupId: null,
      deletingCardId: null,

      // Data
      cardsById: {},
      doc: deepClone(docModel),
      exerciseId: null, // Track the exercise ID after load/save

      // Internal
      cardsUnsub: null,
      pruneDebounceTimer: null,
    }
  },

  computed: {
    ...mapState(['userProfile', 'currentUser', 'currentProject', 'currentProjectModel']),

    isDev() {
      return process.env.NODE_ENV === 'development'
    },

    pid() {
      return this.$route.params.pid || this.currentProjectModel?.id || null
    },

    eid() {
      // Route param 'create' means new exercise, not a valid eid
      const routeEid = this.$route.params.eid
      if (routeEid && routeEid !== 'create') {
        return routeEid
      }
      return this.exerciseId || null
    },

    isEditingCard() {
      return !!this.editingCardId
    },

    editingCard() {
      return this.editingCardId ? this.cardsById[this.editingCardId] : null
    },

    isEditingGroup() {
      return !!this.editingGroupId
    },

    editingGroup() {
      return this.editingGroupId ? this.doc.list.find((g) => g.id === this.editingGroupId) : null
    },

    deletingGroup() {
      return this.deletingGroupId ? this.doc.list.find((g) => g.id === this.deletingGroupId) : null
    },

    deletingGroupCardCount() {
      if (!this.deletingGroup) return 0
      return this.countNestedCards(this.deletingGroup.children)
    },

    deletingCard() {
      return this.deletingCardId ? this.cardsById[this.deletingCardId] : null
    },

    themeColor() {
      return this.doc.theme === 'light' ? 'theme--dark grey lighten-4' : 'grey darken-3'
    },

    allCards() {
      return Object.values(this.cardsById)
    },

    activeTabs() {
      return this.adminMode === 'admin' ? this.steps : this.steps.slice(0, 2)
    },

    activeCards() {
      return this.allCards.filter((c) => !c.deleted)
    },

    deletedCards() {
      return this.allCards.filter((c) => c.deleted)
    },

    deletedCardIds() {
      return this.deletedCards.map((c) => c.id)
    },

    // Deleted card nodes in the format StructureCol expects
    deletedCardNodes() {
      return this.deletedCards.map((c) => ({ id: c.id, children: [] }))
    },

    filteredCards() {
      const query = (this.cardSearch || '').trim().toLowerCase()
      if (!query) return this.activeCards

      return this.activeCards.filter((card) => {
        const searchableText = [
          card.name,
          card.title,
          card.sentence,
          card.body,
          card.historical,
          ...(card.categories || []),
          ...(card.tags || []),
          ...(card.assets || []),
          ...(card.audiences || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return searchableText.includes(query)
      })
    },

    /**
     * Organizes active cards by their group ID
     * Returns an object with group IDs as keys and arrays of cards as values
     * Includes an 'unassigned' key for cards without a group
     */
    cardsByGroup() {
      const grouped = { unassigned: [] }

      // Initialize groups from doc.list
      for (const group of this.doc.list || []) {
        grouped[group.id] = []
      }

      // Sort cards into groups
      for (const card of this.activeCards) {
        const groupId = card.group || null
        if (groupId && grouped[groupId]) {
          grouped[groupId].push(card)
        } else {
          grouped.unassigned.push(card)
        }
      }

      return grouped
    },

    /**
     * Filter cards by group based on search query
     */
    filteredCardsByGroup() {
      const query = (this.cardSearch || '').trim().toLowerCase()
      const result = {}

      for (const [groupId, cards] of Object.entries(this.cardsByGroup)) {
        if (!query) {
          result[groupId] = cards
        } else {
          result[groupId] = cards.filter((card) => {
            const searchableText = [
              card.name,
              card.title,
              card.sentence,
              card.body,
              card.historical,
              ...(card.categories || []),
              ...(card.tags || []),
              ...(card.assets || []),
              ...(card.audiences || []),
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
            return searchableText.includes(query)
          })
        }
      }

      return result
    },

    // Set of placed card IDs for O(1) lookup
    placedCardIdsSet() {
      const placed = new Set()

      const collectIds = (nodes) => {
        if (!Array.isArray(nodes)) return
        for (const node of nodes) {
          if (node?.id) {
            placed.add(node.id)
            collectIds(node.children)
          }
        }
      }

      for (const group of this.doc.list || []) {
        collectIds(group.children)
      }

      return placed
    },

    structureColAttrs() {
      return {
        cols: this.doc.cols,
        controls: this.doc.controls,
        theme: this.doc.theme,
        graph: this.doc.graph,
        children: this.doc.children,
        type: this.doc.type,
        note: this.doc.note,
        historic: this.doc.historic,
        stats: this.doc.stats,
        tags: this.doc.tags,
        add: true,
        local: this.doc.local,
        minimal: this.minimalView, // Show only titles when true
      }
    },

    /**
     * View-only attributes - same as structureColAttrs but read-only
     * No controls, no add buttons, no dragging
     */
    viewColAttrs() {
      return {
        cols: this.doc.cols,
        controls: false, // No edit/delete/clone buttons
        theme: this.doc.theme,
        graph: this.doc.graph,
        children: this.doc.children,
        type: this.doc.type,
        note: this.doc.note,
        historic: this.doc.historic,
        stats: this.doc.stats,
        tags: this.doc.tags,
        add: false, // No add button
        local: this.doc.local,
        readonly: true, // View-only mode - no dragging, no drop zones
      }
    },

    /**
     * Options for the group filter dropdown in View tab
     */
    groupFilterOptions() {
      return (this.doc.list || []).map((group) => ({
        id: group.id,
        title: group.title || 'Untitled',
      }))
    },

    /**
     * Filtered groups based on selectedGroups
     * If no groups selected, show all
     */
    filteredViewGroups() {
      if (!this.selectedGroups.length) {
        return this.doc.list || []
      }
      return (this.doc.list || []).filter((group) => this.selectedGroups.includes(group.id))
    },

    /**
     * Flat list of all cards from filtered groups for flat view mode
     * Collects all cards including nested ones
     */
    flatViewCards() {
      const cards = []

      const collectCards = (nodes) => {
        if (!Array.isArray(nodes)) return
        for (const node of nodes) {
          if (node?.id && this.cardsById[node.id]) {
            cards.push(this.cardsById[node.id])
          }
          if (node?.children) {
            collectCards(node.children)
          }
        }
      }

      for (const group of this.filteredViewGroups) {
        collectCards(group.children || [])
      }

      return cards
    },

    debugInfo() {
      return {
        editingCardId: this.editingCardId,
        cardCount: this.allCards.length,
        activeCount: this.activeCards.length,
        deletedCount: this.deletedCards.length,
        groupCount: this.doc.list?.length || 0,
      }
    },
  },

  watch: {
    // Deep watch on doc.list to ensure nested changes are tracked
    'doc.list': {
      handler(newVal) {
        // Ensure all nodes have children arrays for proper reactivity
        const ensureNestedChildren = (children) => {
          if (!Array.isArray(children)) return
          for (const child of children) {
            if (child && typeof child === 'object') {
              if (!Array.isArray(child.children)) {
                this.$set(child, 'children', [])
              } else {
                ensureNestedChildren(child.children)
              }
            }
          }
        }

        for (const group of newVal || []) {
          if (group && !Array.isArray(group.children)) {
            this.$set(group, 'children', [])
          } else if (group) {
            ensureNestedChildren(group.children)
          }
        }
      },
      deep: true,
    },
  },

  created() {
    // Initialize doc with defaults
    this.doc = deepClone(docModel)
  },

  beforeDestroy() {
    this.cleanup()
  },

  methods: {
    // ─────────────────────────────────────────────────────────────────
    // Lifecycle & Events
    // ─────────────────────────────────────────────────────────────────

    cleanup() {
      if (this.cardsUnsub) {
        this.cardsUnsub()
        this.cardsUnsub = null
      }
      if (this.pruneDebounceTimer) {
        clearTimeout(this.pruneDebounceTimer)
        this.pruneDebounceTimer = null
      }
    },

    onReady() {
      // Component is ready
    },

    onError(error) {
      console.error('[UserOfferSystemId] Error:', error)
    },

    onSaved(payload) {
      // Store the exercise ID if provided
      if (payload?.id) {
        this.exerciseId = payload.id
        // Redirect from /create to actual /:eid route
        this.redirectIfCreate(payload.id)
      }
      // Only show saved dialog if not auto-saving
      if (!this.isAutoSaving) {
        this.savedDialog = true
      }
      // Subscribe to cards if not already subscribed and we now have an eid
      if (this.eid && !this.cardsUnsub) {
        this.subscribeToCards()
      }
    },

    prevStep() {
      console.log('prev step')
      this.currentStep--
    },

    onSavedNext(payload) {
      // Store the exercise ID if provided
      if (payload?.id) {
        this.exerciseId = payload.id
        // Redirect from /create to actual /:eid route
        this.redirectIfCreate(payload.id)
      }
      this.currentStep++
      // Subscribe to cards if not already subscribed and we now have an eid
      if (this.eid && !this.cardsUnsub) {
        this.subscribeToCards()
      }
    },

    /**
     * Redirect from /create URL to actual /:eid URL after saving
     */
    redirectIfCreate(newEid) {
      if (this.$route.params.eid === 'create' && newEid) {
        // Replace 'create' in the current path with the actual ID
        const newPath = this.$route.path.replace('/create', `/${newEid}`)
        this.$router.replace(newPath)
      }
    },

    onLoadedRef() {
      // Handle loaded references if needed
    },

    onModifyEx() {
      // Handle modified exercises if needed
    },

    onLoadedEx(items) {
      if (!items?.length) return

      // Store the exercise ID for card operations
      if (items[0].id) {
        this.exerciseId = items[0].id
      }

      // Merge loaded exercise data into local doc
      this.mergeExerciseData(items[0].doc)

      // Normalize layout structure
      this.normalizeLayout()

      // Start listening for card changes
      this.subscribeToCards()
    },

    // ─────────────────────────────────────────────────────────────────
    // Cards Subscription
    // ─────────────────────────────────────────────────────────────────

    subscribeToCards() {
      this.cleanup()

      // Ensure we have valid, non-empty pid and eid
      const pid = this.pid
      const eid = this.eid

      if (!pid || typeof pid !== 'string' || !pid.trim()) {
        console.warn('[UserOfferSystemId] Invalid pid, cannot subscribe to cards', { pid })
        return
      }

      // Don't subscribe if eid is 'create' or invalid
      if (!eid || typeof eid !== 'string' || !eid.trim() || eid === 'create') {
        console.warn('[UserOfferSystemId] Invalid eid, cannot subscribe to cards - save the exercise first', { eid })
        return
      }

      console.log('[UserOfferSystemId] Subscribing to cards with pid:', pid, 'eid:', eid)

      // Subscribe to cards for this project and exercise
      this.cardsUnsub = fb.db
        .collection('offerSystemExCollectionCards')
        .where('pid', '==', pid)
        .where('eid', '==', eid)
        .onSnapshot(
          (snapshot) => {
            const cards = {}
            snapshot.forEach((doc) => {
              cards[doc.id] = { id: doc.id, ...doc.data() }
            })
            this.cardsById = cards
            console.log('[UserOfferSystemId] Cards loaded:', Object.keys(cards).length, 'cards for eid:', eid)

            // Debounce layout pruning to avoid rapid mutations
            this.debouncedPruneLayout()
          },
          (error) => {
            console.error('[UserOfferSystemId] Cards subscription error:', error)
          }
        )
    },

    debouncedPruneLayout() {
      if (this.pruneDebounceTimer) {
        clearTimeout(this.pruneDebounceTimer)
      }
      this.pruneDebounceTimer = setTimeout(() => {
        this.pruneOrphanedCardIds()
      }, 150)
    },

    // ─────────────────────────────────────────────────────────────────
    // Layout Management
    // ─────────────────────────────────────────────────────────────────

    /**
     * Remove card IDs from layout that no longer exist in cardsById
     * Uses iterative approach to avoid stack overflow on deep nesting
     */
    pruneOrphanedCardIds() {
      const validIds = new Set(Object.keys(this.cardsById))

      const pruneNodes = (nodes) => {
        if (!Array.isArray(nodes)) return []

        const result = []
        const stack = [...nodes]
        const processed = new WeakSet()

        while (stack.length > 0) {
          const node = stack.shift()

          if (!node || processed.has(node)) continue
          processed.add(node)

          if (!node.id || !validIds.has(node.id)) {
            continue
          }

          const prunedChildren = node.children ? pruneNodes(node.children) : []
          result.push({
            id: node.id,
            children: prunedChildren,
          })
        }

        return result
      }

      for (const group of this.doc.list || []) {
        const pruned = pruneNodes(group.children || [])
        if (JSON.stringify(pruned) !== JSON.stringify(group.children)) {
          group.children = pruned
        }
      }
    },

    /**
     * Recursively normalize a children array to ensure proper { id, children } structure
     */
    normalizeChildren(children) {
      if (!Array.isArray(children)) return []

      return children
        .map((child) => {
          if (typeof child === 'string') {
            return { id: child, children: [] }
          }
          if (child && typeof child === 'object' && child.id) {
            return {
              id: String(child.id),
              // Recursively normalize nested children
              children: this.normalizeChildren(child.children),
            }
          }
          return null
        })
        .filter(Boolean)
    },

    normalizeLayout() {
      for (const group of this.doc.list || []) {
        if (!Array.isArray(group.children)) {
          this.$set(group, 'children', [])
          continue
        }

        // Recursively convert all levels to { id, children } structure
        group.children = this.normalizeChildren(group.children)
      }
    },

    // ─────────────────────────────────────────────────────────────────
    // Card CRUD
    // ─────────────────────────────────────────────────────────────────

    isCardPlaced(cardId) {
      return this.placedCardIdsSet.has(cardId)
    },

    // ─────────────────────────────────────────────────────────────────
    // Card Dialog
    // ─────────────────────────────────────────────────────────────────

    async openAddCard() {
      // Auto-save exercise if no eid exists yet
      if (!this.eid) {
        await this.ensureExerciseSaved()
        if (!this.eid) {
          console.warn('[UserOfferSystemId] Could not save exercise, cannot add card')
          return
        }
      }
      this.editingCardId = null
      this.targetGroupId = null
      this.cardDialog = true
    },

    async openAddCardIntoGroup(groupId) {
      // Auto-save exercise if no eid exists yet
      if (!this.eid) {
        await this.ensureExerciseSaved()
        if (!this.eid) {
          console.warn('[UserOfferSystemId] Could not save exercise, cannot add card')
          return
        }
      }

      // Expand the panel for this group if not already open
      if (groupId) {
        const groupIndex = this.doc.list.findIndex((g) => g.id === groupId)
        if (groupIndex !== -1 && !this.step2Panels.includes(groupIndex)) {
          this.step2Panels.push(groupIndex)
        }
      }

      this.editingCardId = null
      this.targetGroupId = groupId || null
      this.cardDialog = true
    },

    /**
     * Ensures the exercise is saved and has an eid
     * Returns a promise that resolves when the save is complete
     */
    ensureExerciseSaved() {
      return new Promise((resolve) => {
        if (this.eid) {
          resolve()
          return
        }

        this.isSavingExercise = true
        this.isAutoSaving = true

        // Set up a one-time watcher for exerciseId
        const unwatch = this.$watch('exerciseId', (newId) => {
          if (newId) {
            unwatch()
            this.isSavingExercise = false
            this.isAutoSaving = false
            // Give a moment for the cards subscription to set up
            this.$nextTick(() => {
              resolve()
            })
          }
        })

        // Trigger save via the exercise logic component (use true to call onSaved, not onSavedNext which increments step)
        if (this.$refs.exerciseLogic) {
          this.$refs.exerciseLogic.saveExercise(true)
        } else {
          console.error('[UserOfferSystemId] Exercise logic ref not found')
          unwatch()
          this.isSavingExercise = false
          this.isAutoSaving = false
          resolve()
        }

        // Timeout fallback to avoid hanging
        setTimeout(() => {
          unwatch()
          this.isSavingExercise = false
          this.isAutoSaving = false
          resolve()
        }, 5000)
      })
    },

    openEditCard(cardId) {
      this.editingCardId = cardId
      this.targetGroupId = null
      this.cardDialog = true
    },

    closeCardDialog() {
      this.cardDialog = false
      this.editingCardId = null
      this.targetGroupId = null
      if (this.$refs.cardDialogForm?.reset) {
        this.$refs.cardDialogForm.reset()
      }
    },

    // ─────────────────────────────────────────────────────────────────
    // Group Dialog
    // ─────────────────────────────────────────────────────────────────

    openAddGroup() {
      this.editingGroupId = null
      this.groupDialog = true
    },

    openEditGroup(groupId) {
      this.editingGroupId = groupId
      this.groupDialog = true
    },

    closeGroupDialog() {
      this.groupDialog = false
      this.editingGroupId = null
      if (this.$refs.groupDialogForm?.reset) {
        this.$refs.groupDialogForm.reset()
      }
    },

    upsertGroup(draft) {
      if (!draft.title?.trim()) return

      if (this.isEditingGroup) {
        // Update existing group
        const index = this.doc.list.findIndex((g) => g.id === this.editingGroupId)
        if (index !== -1) {
          const existing = this.doc.list[index]
          this.$set(this.doc.list, index, {
            ...existing,
            title: draft.title,
            name: draft.name,
            description: draft.description,
          })
        }
      } else {
        // Create new group
        const newGroup = {
          id: generateId('group'),
          title: draft.title,
          name: draft.name,
          description: draft.description || '',
          children: [],
        }
        this.$set(this.doc, 'list', [...this.doc.list, newGroup])
      }

      this.closeGroupDialog()
    },

    // ─────────────────────────────────────────────────────────────────
    // Delete Group (with confirmation)
    // ─────────────────────────────────────────────────────────────────

    confirmDeleteGroup(groupId) {
      this.deletingGroupId = groupId
      this.deleteGroupDialog = true
    },

    cancelDeleteGroup() {
      this.deleteGroupDialog = false
      this.deletingGroupId = null
    },

    /**
     * Count all cards in a nested children structure
     */
    countNestedCards(children) {
      if (!Array.isArray(children)) return 0
      let count = 0
      for (const node of children) {
        if (node?.id) {
          count++
          if (Array.isArray(node.children)) {
            count += this.countNestedCards(node.children)
          }
        }
      }
      return count
    },

    /**
     * Collect all card IDs from a nested children structure
     */
    collectNestedCardIds(children, ids = []) {
      if (!Array.isArray(children)) return ids
      for (const node of children) {
        if (node?.id) {
          ids.push(node.id)
          if (Array.isArray(node.children)) {
            this.collectNestedCardIds(node.children, ids)
          }
        }
      }
      return ids
    },

    /**
     * Execute group deletion - preserves all data
     */
    async executeDeleteGroup() {
      if (!this.deletingGroupId) return

      const group = this.doc.list.find((g) => g.id === this.deletingGroupId)
      if (!group) {
        this.cancelDeleteGroup()
        return
      }

      try {
        // 1. Collect all nested card IDs
        const cardIds = this.collectNestedCardIds(group.children || [])

        // 2. Store the complete group with structure in deletedGroups for recovery
        const deletedGroupRecord = {
          ...deepClone(group),
          deletedAt: new Date().toISOString(),
          cardCount: cardIds.length,
        }

        if (!Array.isArray(this.doc.deletedGroups)) {
          this.$set(this.doc, 'deletedGroups', [])
        }
        this.doc.deletedGroups.push(deletedGroupRecord)

        // 3. Mark all cards as deleted in Firebase (soft delete)
        const batchPromises = cardIds.map((cardId) =>
          fb.db
            .collection('offerSystemExCollectionCards')
            .doc(cardId)
            .set(
              {
                deleted: true,
                deletedFromGroup: group.id,
                deletedAt: new Date(),
                updatedAt: new Date(),
              },
              { merge: true }
            )
        )

        await Promise.all(batchPromises)

        // 4. Remove the group from the list
        const index = this.doc.list.findIndex((g) => g.id === this.deletingGroupId)
        if (index !== -1) {
          this.doc.list.splice(index, 1)
        }

        console.log(`[UserOfferSystemId] Deleted group "${group.title}" with ${cardIds.length} cards`)
      } catch (error) {
        console.error('[UserOfferSystemId] Failed to delete group:', error)
      }

      this.cancelDeleteGroup()
    },

    deleteGroup(groupId) {
      this.confirmDeleteGroup(groupId)
    },

    async upsertCard(draft) {
      const pid = this.pid
      const eid = this.eid

      // Validate pid and eid are valid non-empty strings
      if (!pid || typeof pid !== 'string' || !pid.trim()) {
        console.error('[UserOfferSystemId] Cannot save card: invalid pid', { pid })
        alert('Error: Project ID is missing. Please refresh the page.')
        return
      }

      if (!eid || typeof eid !== 'string' || !eid.trim()) {
        console.error('[UserOfferSystemId] Cannot save card: invalid eid', { eid })
        alert('Please save the exercise first before adding items.')
        return
      }

      try {
        const payload = {
          ...deepClone(draft),
          pid: this.pid,
          eid: this.eid,
          deleted: false,
          updatedAt: new Date(),
        }

        // Remove local id field - Firestore doc id is source of truth
        delete payload.id

        let cardId
        // Handle single group (new) or groups array (legacy)
        const assignedGroup = draft.group || (draft.groups && draft.groups[0]) || null

        if (this.isEditingCard) {
          cardId = this.editingCardId

          // Get the old group before saving
          const oldCard = this.cardsById[cardId]
          const oldGroup = oldCard?.group || null

          await fb.db
            .collection('offerSystemExCollectionCards')
            .doc(cardId)
            .set(payload, { merge: true })

          // Only sync layout if the group has changed (to preserve nesting)
          if (assignedGroup && assignedGroup !== oldGroup) {
            this.syncCardToGroup(cardId, assignedGroup)
          }
        } else {
          payload.createdAt = new Date()
          const ref = await fb.db.collection('offerSystemExCollectionCards').add(payload)
          cardId = ref.id

          // Add to assigned group, targetGroupId, or first group
          const targetGroup = assignedGroup || this.targetGroupId || null
          this.addCardToLayout(cardId, targetGroup)
        }

        // Reset form and close dialog
        this.closeCardDialog()
      } catch (error) {
        console.error('[UserOfferSystemId] Failed to save card:', error)
      }
    },

    // ─────────────────────────────────────────────────────────────────
    // Delete Card (with confirmation)
    // ─────────────────────────────────────────────────────────────────

    confirmDeleteCard(cardId) {
      this.deletingCardId = cardId
      this.deleteCardDialog = true
    },

    cancelDeleteCard() {
      this.deleteCardDialog = false
      this.deletingCardId = null
    },

    async executeDeleteCard() {
      if (this.deletingCardId) {
        await this.softDeleteCard(this.deletingCardId)
      }
      this.cancelDeleteCard()
    },

    async softDeleteCard(cardId) {
      if (!cardId) return

      try {
        await fb.db
          .collection('offerSystemExCollectionCards')
          .doc(cardId)
          .set({ deleted: true, updatedAt: new Date() }, { merge: true })

        this.removeCardFromLayout(cardId)
      } catch (error) {
        console.error('[UserOfferSystemId] Failed to delete card:', error)
      }
    },

    /**
     * Clone a card - creates a copy with a new ID
     * Places the clone right after the original in the same nested position
     * @param {string} cardId - The card ID to clone
     */
    async cloneCard(cardId) {
      if (!cardId) return

      const originalCard = this.cardsById[cardId]
      if (!originalCard) {
        console.error('[UserOfferSystemId] Cannot clone: card not found', cardId)
        return
      }

      try {
        // Create a copy of the card data
        const clonedData = deepClone(originalCard)

        // Remove the original ID and update metadata
        delete clonedData.id
        clonedData.name = `${clonedData.name || 'Untitled'} (Copy)`
        clonedData.createdAt = new Date()
        clonedData.updatedAt = new Date()
        clonedData.pid = this.pid
        clonedData.eid = this.eid
        clonedData.deleted = false

        // Save the cloned card to Firebase
        const ref = await fb.db.collection('offerSystemExCollectionCards').add(clonedData)
        const newCardId = ref.id

        // Insert the cloned card right after the original in the same nested position
        const inserted = this.insertCardAfter(cardId, newCardId)

        // Fallback: if couldn't find original position, add to group
        if (!inserted) {
          const groupId = originalCard.group || null
          this.addCardToLayout(newCardId, groupId)
        }

        console.log('[UserOfferSystemId] Card cloned:', cardId, '->', newCardId)
      } catch (error) {
        console.error('[UserOfferSystemId] Failed to clone card:', error)
      }
    },

    /**
     * Insert a new card right after an existing card in the nested structure
     * @param {string} afterCardId - The card ID to insert after
     * @param {string} newCardId - The new card ID to insert
     * @returns {boolean} True if successfully inserted
     */
    insertCardAfter(afterCardId, newCardId) {
      const newNode = { id: newCardId, children: [] }

      // Recursive function to find and insert
      const insertInNodes = (nodes) => {
        if (!Array.isArray(nodes)) return false

        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i]?.id === afterCardId) {
            // Found it! Insert the new node right after
            nodes.splice(i + 1, 0, newNode)
            return true
          }

          // Search in children
          if (nodes[i]?.children && insertInNodes(nodes[i].children)) {
            return true
          }
        }

        return false
      }

      // Search through all groups
      for (const group of this.doc.list || []) {
        if (insertInNodes(group.children || [])) {
          return true
        }
      }

      return false
    },

    addCardToLayout(cardId, groupId = null) {
      if (!cardId) return

      const newNode = { id: cardId, children: [] }

      // Try to add to specified group
      if (groupId) {
        const group = this.doc.list?.find((g) => g.id === groupId)
        if (group) {
          if (!Array.isArray(group.children)) {
            this.$set(group, 'children', [])
          }
          group.children.push(newNode)
          return
        }
      }

      // Add to first group if exists
      if (this.doc.list?.length) {
        const firstGroup = this.doc.list[0]
        if (!Array.isArray(firstGroup.children)) {
          this.$set(firstGroup, 'children', [])
        }
        firstGroup.children.push(newNode)
        return
      }

      // Create new group
      this.doc.list = [
        {
          id: generateId('group'),
          title: 'Unassigned',
          name: 'unassigned',
          children: [newNode],
        },
      ]
    },

    removeCardFromLayout(cardId) {
      const removeFromNodes = (nodes, visited = new WeakSet()) => {
        if (!Array.isArray(nodes)) return []

        return nodes
          .filter((n) => n && n.id !== cardId)
          .map((n) => {
            if (visited.has(n)) return null
            visited.add(n)
            return {
              id: n.id,
              children: removeFromNodes(n.children || [], visited),
            }
          })
          .filter(Boolean)
      }

      for (const group of this.doc.list || []) {
        group.children = removeFromNodes(group.children || [])
      }
    },

    /**
     * Move card to a single group (removes from all others first)
     * @param {string} cardId - The card ID
     * @param {string} groupId - The target group ID
     */
    syncCardToGroup(cardId, groupId) {
      if (!cardId) return

      // Remove card from all groups first
      this.removeCardFromLayout(cardId)

      // Add to the specified group
      if (groupId) {
        this.addCardToLayout(cardId, groupId)
      }
    },

    /**
     * Check if any scores are non-zero
     * @param {Object} scores - The scores object
     * @returns {boolean} True if any score is > 0
     */
    hasNonZeroScores(scores) {
      if (!scores || typeof scores !== 'object') return false
      return Object.values(scores).some((val) => val > 0)
    },

    /**
     * Find which group contains a card (returns first match)
     * @param {string} cardId - The card ID to find
     * @returns {string|null} Group ID or null if not found
     */
    findGroupForCard(cardId) {
      if (!cardId) return null

      const findInChildren = (nodes) => {
        if (!Array.isArray(nodes)) return false
        return nodes.some((n) => n?.id === cardId || findInChildren(n?.children))
      }

      for (const group of this.doc.list || []) {
        if (findInChildren(group.children)) {
          return group.id
        }
      }

      return null
    },

    /**
     * Handle card moved via drag and drop - sync group field to Firebase
     */
    async onCardMoved({ cardId }) {
      if (!cardId) return

      // Find which group the card is now in
      const currentGroup = this.findGroupForCard(cardId)

      // Update the card's group field in Firebase
      try {
        await fb.db
          .collection('offerSystemExCollectionCards')
          .doc(cardId)
          .set({ group: currentGroup, updatedAt: new Date() }, { merge: true })
      } catch (error) {
        console.error('[UserOfferSystemId] Failed to sync card group:', error)
      }
    },

    // ─────────────────────────────────────────────────────────────────
    // Group Management (for StructureCol events)
    // ─────────────────────────────────────────────────────────────────

    onDeleteList({ id }) {
      // Use the same confirmation flow as deleteGroup
      this.confirmDeleteGroup(id)
    },

    // ─────────────────────────────────────────────────────────────────
    // Data Merging
    // ─────────────────────────────────────────────────────────────────

    mergeExerciseData(data) {
      if (!data) return

      for (const [key, value] of Object.entries(data)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Deep merge objects (like 'local')
          if (!this.doc[key] || typeof this.doc[key] !== 'object') {
            this.$set(this.doc, key, {})
          }
          for (const [innerKey, innerValue] of Object.entries(value)) {
            this.$set(this.doc[key], innerKey, innerValue)
          }
        } else {
          // Direct assignment for primitives and arrays
          this.$set(this.doc, key, value)
        }
      }

      // Ensure doc.list is always a valid array
      if (!Array.isArray(this.doc.list)) {
        this.$set(this.doc, 'list', [])
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.user-offer-system {
  .auto {
    display: flex;
    flex-wrap: nowrap;

    .col-auto {
      width: auto;
      flex: 1;
    }
  }

  .group-card,
  .item-card {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #ffc107;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &__actions {
      display: flex;
      gap: 4px;
      opacity: 0.6;
      transition: opacity 0.2s ease;
    }

    &:hover &__actions {
      opacity: 1;
    }
  }

  .item-card--unassigned {
    border-color: #ff9800;
    border-style: dashed;
  }

  .step2-panel-header {
    .step2-add-btn {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &:hover .step2-add-btn {
      opacity: 1;
    }
  }

  .debug-pre {
    font-size: 12px;
    max-height: 300px;
    overflow: auto;
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
  }

  .flat-view-card {
    height: 100%;

    .color-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .pre-wrap {
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
}
</style>